import React, { useEffect, useState } from 'react';
import { Input, Button, message, Icon, Alert, Switch } from 'antd';
import { getMetrcCreds, saveMetrcCreds, runLicenseScrape } from '../../_shared/services/api.service';

export const MetrcCredsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [editingVendorKey, setEditingVendorKey] = useState(false);
  const [editingUserKey, setEditingUserKey] = useState(false);
  const [state, setState] = useState({ username: '', stateHost: '', email: '', hasPassword: false, hasVendorKey: false, hasUserKey: false, sandbox: false });
  const [password, setPassword] = useState('');
  const [vendorKey, setVendorKey] = useState('');
  const [userKey, setUserKey] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const data = await getMetrcCreds();
      setState({
        username: data.username || '',
        stateHost: data.stateHost || 'mn',
        email: data.email || '',
        hasPassword: !!data.hasPassword,
        hasVendorKey: !!data.hasVendorKey,
        hasUserKey: !!data.hasUserKey,
        sandbox: !!data.sandbox,
      });
    } catch (err) {
      message.error('Failed to load METRC credentials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      setSaving(true);
      const payload = {
        username: state.username,
        stateHost: state.stateHost,
        email: state.email,
        sandbox: state.sandbox,
      };
      if (editingPassword && password) payload.password = password;
      if (editingVendorKey && vendorKey) payload.vendorKey = vendorKey;
      if (editingUserKey && userKey) payload.userKey = userKey;
      await saveMetrcCreds(payload);
      message.success('METRC credentials saved');
      setEditingPassword(false);
      setPassword('');
      setEditingVendorKey(false);
      setVendorKey('');
      setEditingUserKey(false);
      setUserKey('');
      await load();
    } catch (err) {
      message.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    try {
      setTesting(true);
      await runLicenseScrape();
      message.success('Scrape completed');
    } catch (err) {
      message.error('Scrape failed — check error notifications');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{ marginBottom: 20, padding: 16, border: '1px solid #e8e8e8', borderRadius: 4, background: '#fafafa' }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>METRC Login</div>
      <Alert
        type="info"
        showIcon
        message="Used by the automated license scraper. Stored encrypted. METRC passwords rotate every 90 days — update here when that happens."
        style={{ marginBottom: 12 }}
      />

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 12, color: '#666' }}>State Host</label>
        <Input
          value={state.stateHost}
          onChange={e => setState({ ...state, stateHost: e.target.value })}
          placeholder="mn"
          disabled={loading}
          addonBefore="https://"
          addonAfter=".metrc.com"
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 12, color: '#666' }}>Username</label>
        <Input
          value={state.username}
          onChange={e => setState({ ...state, username: e.target.value })}
          disabled={loading}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 12, color: '#666' }}>E-mail <span style={{ color: '#aaa' }}>(only required by some states, e.g. CO)</span></label>
        <Input
          value={state.email}
          onChange={e => setState({ ...state, email: e.target.value })}
          disabled={loading}
          placeholder="Leave blank if not required"
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: '#666' }}>Password</label>
        {!editingPassword ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input
              value={state.hasPassword ? '••••••••••' : ''}
              placeholder={state.hasPassword ? '' : 'Not set'}
              disabled
            />
            <Button size="small" onClick={() => setEditingPassword(true)}>
              <Icon type="edit" /> Change
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input.Password
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="New password"
              autoFocus
            />
            <Button size="small" onClick={() => { setEditingPassword(false); setPassword(''); }}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid #e8e8e8', margin: '8px 0 12px' }} />
      <div style={{ fontWeight: 600, marginBottom: 8 }}>METRC REST API (Transfer Hub)</div>
      <Alert
        type="info"
        showIcon
        message="Pushes transfer status to METRC (depart/check-in/arrive) for THIS deployment. The Environment toggle + State Host above decide the endpoint (e.g. https://sandbox-api-co.metrc.com vs https://api-co.metrc.com). Vendor API Key is GPS One's integrator key for this state. User API Key is harvested by the scrape (prod) — for a sandbox deployment, enter the sandbox User API Key manually."
        style={{ marginBottom: 12 }}
      />

      <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Switch
          checked={!state.sandbox}
          disabled={loading}
          onChange={checked => setState({ ...state, sandbox: !checked })}
        />
        <span>
          Environment: <b>{state.sandbox ? 'Sandbox' : 'Production'}</b>
          <span style={{ color: '#aaa' }}>
            {' '}— hub will call {`https://${state.sandbox ? 'sandbox-' : ''}api-${state.stateHost || '<state>'}.metrc.com`}
          </span>
        </span>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 12, color: '#666' }}>Vendor API Key <span style={{ color: '#aaa' }}>(GPS integrator key for this state)</span></label>
        {!editingVendorKey ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input
              value={state.hasVendorKey ? '••••••••••' : ''}
              placeholder={state.hasVendorKey ? '' : 'Not set'}
              disabled
            />
            <Button size="small" onClick={() => setEditingVendorKey(true)}>
              <Icon type="edit" /> Change
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input.Password
              value={vendorKey}
              onChange={e => setVendorKey(e.target.value)}
              placeholder="Vendor API Key"
              autoFocus
            />
            <Button size="small" onClick={() => { setEditingVendorKey(false); setVendorKey(''); }}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: '#666' }}>User API Key <span style={{ color: '#aaa' }}>(auto-harvested by scrape for prod; set manually for sandbox)</span></label>
        {!editingUserKey ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input
              value={state.hasUserKey ? '••••••••••' : ''}
              placeholder={state.hasUserKey ? '' : 'Not set — run a scrape (prod) or enter a sandbox key'}
              disabled
            />
            <Button size="small" onClick={() => setEditingUserKey(true)}>
              <Icon type="edit" /> Change
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input.Password
              value={userKey}
              onChange={e => setUserKey(e.target.value)}
              placeholder="User API Key (e.g. sandbox)"
              autoFocus
            />
            <Button size="small" onClick={() => { setEditingUserKey(false); setUserKey(''); }}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="primary" onClick={save} loading={saving} disabled={loading}>
          Save METRC Login
        </Button>
        {/* Not gated on the METRC password: the server picks the retriever at call
            time, so on a deployment with no METRC creds (e.g. CO) this still runs
            that state's own scrape. Gating it left those envs with no manual run. */}
        <Button onClick={runTest} loading={testing} disabled={loading}>
          Run Scrape Now
        </Button>
      </div>
    </div>
  );
};
